import NavigationIcon from '@mui/icons-material/Navigation';
import ZoomInMapIcon from '@mui/icons-material/ZoomInMap';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import L from 'leaflet';
import throttle from 'lodash/throttle';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { CameraSelectionContext, PTZContext } from 'components/ExtendedVideoPlayer/context';
import { JoystickContext } from 'contexts';
import { useTranslation } from 'react-i18next';
import usePTZStyles from './styles';
import { CameraSpeedGear, PTZProps, Zoom } from './types';

enum PanTiltSpeed {
  POSITIVE = 50,
  NEGATIVE = -50,
}

export interface GamepadRef {
  [key: number]: Gamepad;
}

const PTZ: React.FunctionComponent<PTZProps> = ({ cameraId }) => {
  const { t } = useTranslation('common');
  const classes = usePTZStyles();
  const cameraRef = useRef<HTMLDivElement>(null);
  const [mouseDown, setMouseDown] = useState(false);
  const [zoom, setZoom] = useState<Zoom>({ isZoomingIn: false, isZoomingOut: false });
  const { setPanTiltSpeed, setZoomSpeed, panSpeed, tiltSpeed } = useContext(PTZContext);
  const { selectedCameraId, setSelectedCameraId } = useContext(CameraSelectionContext);
  const { axisState } = useContext(JoystickContext);

  const isSelected = cameraId === selectedCameraId;
  const pointerRef = useRef<SVGSVGElement>(null);
  const zoomTimeout = useRef<NodeJS.Timeout | null>(null);
  const numberOfScrollSteps = useRef(0);

  useEffect(() => {
    if (!axisState || !isSelected) {
      return;
    }
    const { axis } = axisState;
    const [pan, tilt, zoom] = axis;

    const newPanSpeed = pan !== null ? Math.floor(pan * CameraSpeedGear.HIGHEST) : 0;
    const newTiltSpeed = tilt !== null ? Math.floor(tilt * CameraSpeedGear.HIGHEST) : 0;
    const newZoomSpeed = zoom !== null ? Math.floor(zoom * CameraSpeedGear.HIGHEST) : 0;

    setPanTiltSpeed(newPanSpeed, newTiltSpeed);

    setZoomSpeed(newZoomSpeed);
    if (newZoomSpeed === 0) {
      setZoom({ isZoomingOut: false, isZoomingIn: false });
    } else if (newZoomSpeed > 0) {
      setZoom({ isZoomingOut: false, isZoomingIn: true });
    } else if (newZoomSpeed < 0) {
      setZoom({ isZoomingOut: true, isZoomingIn: false });
    }
  }, [axisState, isSelected, setPanTiltSpeed, setZoomSpeed]);

  const doZoom = useMemo(
    () =>
      throttle(
        () => {
          setZoomSpeed(
            numberOfScrollSteps.current < 0 ? CameraSpeedGear.HIGHEST : -CameraSpeedGear.HIGHEST
          );
          clearTimeout(zoomTimeout.current!);
          zoomTimeout.current = setTimeout(() => {
            setZoomSpeed(0);
            numberOfScrollSteps.current = 0;
            setZoom({ isZoomingOut: false, isZoomingIn: false });
          }, 150);
        },
        100,
        {
          leading: true,
        }
      ),
    [setZoomSpeed, setZoom]
  );

  const setCameraZoom = useCallback(
    (ev: WheelEvent) => {
      if (ev.deltaY > 0) {
        ++numberOfScrollSteps.current;
        setZoom({ isZoomingOut: true, isZoomingIn: false });
      }
      if (ev.deltaY < 0) {
        --numberOfScrollSteps.current;
        setZoom({ isZoomingOut: false, isZoomingIn: true });
      }
      doZoom();
    },
    [doZoom]
  );

  const onWheel = useCallback(
    (event: Event) => {
      L.DomEvent.stopPropagation(event);
      event.preventDefault();
      setCameraZoom(event as WheelEvent);
    },
    [setCameraZoom]
  );

  useEffect(() => {
    const cameraEl = cameraRef.current;
    cameraEl?.addEventListener('wheel', onWheel, { capture: true, passive: false });
    return () => cameraEl?.removeEventListener('wheel', onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraRef.current]);

  const calculateSpeed = (event: React.MouseEvent<HTMLDivElement, MouseEvent> | undefined) => {
    const camera = cameraRef.current as unknown as HTMLDivElement;

    let cameraRect = camera.getBoundingClientRect();

    let x = (event?.clientX ?? 0) - cameraRect.left; //x position within the element.
    let y = (event?.clientY ?? 0) - cameraRect.top; //y position within the element.

    const cameraX = camera.offsetLeft + camera.clientWidth / 2;
    const cameraY = camera.offsetTop + camera.clientHeight / 2;

    const distanceX = x - cameraX;
    const distanceY = y - cameraY;

    const panCoefficient = CameraSpeedGear.HIGHEST / cameraX;
    const tiltCoefficient = CameraSpeedGear.HIGHEST / cameraY;

    const panSpeed = Math.floor(panCoefficient * distanceX);
    const tiltSpeed = -Math.floor(tiltCoefficient * distanceY);

    return { panSpeed, tiltSpeed };
  };

  const cameraMove = (event: React.MouseEvent<HTMLDivElement, MouseEvent> | undefined) => {
    event?.preventDefault();
    event?.stopPropagation();

    const { panSpeed: newPanSpeed, tiltSpeed: newTiltSpeed } = calculateSpeed(event);

    // reducing number of request - sending new request if the speed difference (of the last and new request) is greater that the middle speed
    // or if mouse position is closer to the pole - sending new request if speed difference is greater then the lowest speed

    const tiltSpeedDifference = Math.abs(tiltSpeed - newTiltSpeed);
    const panSpeedDifference = Math.abs(panSpeed - newPanSpeed);

    const isSpeedDifferenceGreater =
      panSpeedDifference > CameraSpeedGear.MIDDLE || tiltSpeedDifference > CameraSpeedGear.MIDDLE;

    const isFirstMove =
      panSpeed === CameraSpeedGear.NEUTRAL && tiltSpeed === CameraSpeedGear.NEUTRAL;

    const isNearPoleSpeedDifferenceGreater =
      isFirstMove ||
      (Math.abs(newPanSpeed) <= CameraSpeedGear.MIDDLE &&
        Math.abs(newTiltSpeed) <= CameraSpeedGear.MIDDLE &&
        (panSpeedDifference >= CameraSpeedGear.LOWEST ||
          tiltSpeedDifference >= CameraSpeedGear.LOWEST));

    if (isSpeedDifferenceGreater || isNearPoleSpeedDifferenceGreater) {
      setPanTiltSpeed(newPanSpeed, newTiltSpeed);
    }
  };

  const stopCameraMovement = () => {
    setMouseDown(false);
    setPanTiltSpeed(CameraSpeedGear.NEUTRAL, CameraSpeedGear.NEUTRAL);
    // send neutral speed after 100ms to help prevent camera from "spinning"
    setTimeout(() => setPanTiltSpeed(CameraSpeedGear.NEUTRAL, CameraSpeedGear.NEUTRAL), 100);
  };

  const setCameraZoomByKeyboard = (ev: React.KeyboardEvent) => {
    if (ev.key === '-') {
      ++numberOfScrollSteps.current;
      setZoom({ isZoomingOut: true, isZoomingIn: false });
    }
    if (ev.key === '+') {
      --numberOfScrollSteps.current;
      setZoom({ isZoomingOut: false, isZoomingIn: true });
    }
    doZoom();
  };

  const calculateDistance = (elem: HTMLDivElement, mouseX: number, mouseY: number) => {
    return Math.floor(
      Math.sqrt(
        Math.pow(mouseX - (elem.offsetLeft + elem.clientWidth / 2), 2) +
          Math.pow(mouseY - (elem.offsetTop + elem.clientHeight / 2), 2)
      )
    );
  };

  const moveIndicator = (event: {
    pageX: any;
    pageY: any;
    currentTarget: any;
    clientX: number;
    clientY: number;
  }) => {
    const camera = cameraRef.current as unknown as HTMLDivElement;
    const pointer = pointerRef.current as unknown as SVGElement;

    let rect = camera.getBoundingClientRect();

    let middle_x = rect.left + rect.width / 2;
    let middle_y = rect.top + rect.height / 2;

    let xT = event.pageX - middle_x;
    let yT = event.pageY - middle_y;

    let x = event.clientX - rect.left; //x position within the element.
    let y = event.clientY - rect.top; //y position within the element.

    var distancePupil = calculateDistance(camera, x, y);

    let angle = Math.atan2(yT, xT) + Math.PI / 2;

    let fontSize = 20;
    const maxFontSize = 60;
    const calcFontSize = (distancePupil / 35) * 6.5;

    if (calcFontSize > fontSize) {
      fontSize = calcFontSize;
    }
    if (fontSize > maxFontSize) {
      fontSize = maxFontSize;
    }
    if (pointer) {
      pointer.style.fontSize = fontSize + 'px';
      pointer.style.transform = `rotate(${angle}rad)`;
    }
  };

  const panTiltCameraByKeyboard = (panSpeed: number, tiltSpeed: number) => {
    setPanTiltSpeed(panSpeed, tiltSpeed);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    const { POSITIVE, NEGATIVE } = PanTiltSpeed;
    switch (event.key) {
      case '+':
      case '-': {
        setCameraZoomByKeyboard(event);
        break;
      }
      case 'ArrowUp': {
        panTiltCameraByKeyboard(0, POSITIVE);
        break;
      }
      case 'ArrowDown': {
        panTiltCameraByKeyboard(0, NEGATIVE);
        break;
      }
      case 'ArrowLeft': {
        panTiltCameraByKeyboard(NEGATIVE, 0);
        break;
      }
      case 'ArrowRight': {
        panTiltCameraByKeyboard(POSITIVE, 0);
        break;
      }
    }
  };

  const renderPTZ = () => {
    return (
      <>
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <div
          role='application'
          aria-label={t('cameraPTZControls')}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex, jsx-a11y/tabindex-no-positive
          tabIndex={1}
          ref={cameraRef}
          className={classes.wrapper}
          onMouseDown={(event) => {
            setSelectedCameraId(cameraId);
            setMouseDown(true);
            cameraMove(event);
            moveIndicator(event);
            cameraRef.current?.focus();
          }}
          onMouseDownCapture={(event) => {
            setMouseDown(true);
            moveIndicator(event);
          }}
          onMouseUp={() => mouseDown && stopCameraMovement()}
          onMouseMove={(event) => {
            if (mouseDown) {
              cameraMove(event);
              moveIndicator(event);
            }
          }}
          onMouseLeave={() => (panSpeed || tiltSpeed) && stopCameraMovement()}
          onKeyDown={onKeyDown}
          onKeyUp={() => stopCameraMovement()}
        />
        {mouseDown && (
          <div className={classes.eyeball}>
            <div className={classes.pupil}>
              <NavigationIcon ref={pointerRef} />
            </div>
          </div>
        )}
        {zoom.isZoomingIn && !mouseDown && (
          <div className={classes.pupil}>
            <ZoomInMapIcon ref={pointerRef} className={classes.zoomIndicator} />
          </div>
        )}
        {zoom.isZoomingOut && !mouseDown && (
          <div className={classes.pupil}>
            <ZoomOutMapIcon ref={pointerRef} className={classes.zoomIndicator} />
          </div>
        )}
      </>
    );
  };
  return renderPTZ();
};

export default PTZ;
